import { buildRoom, roomSeed, type ChunkSet } from '../gen/rooms';
import type { RoomDef } from '../sim/room';
import { MARS_CHUNKS } from './chunks/mars';
import { MOON_CHUNKS } from './chunks/moon';
import { RANGER_ARENA } from './rooms/rangerArena';

/**
 * The planets of a run (GDD: The 5 systems). The free tier is Sol: the Earth tutorial, then the
 * Moon and Mars, where the Ranger waits. Each planet is a few seeded rooms of its own chunks,
 * then its boss's arena if it has one. How a planet looks lives in the renderer, keyed by id.
 */

export type PlanetId = 'moon' | 'mars';

export interface Planet {
  readonly id: PlanetId;
  /** Shown on arrival and in the HUD. Upper case: the pixel font has no lower case. */
  readonly name: string;
  readonly chunks: ChunkSet;
  /** Seeded rooms per visit (GDD: 3–5 a planet, fewer while the slice is short). */
  readonly rooms: number;
  /** Share of the standard gravity: each planet adds one movement idea, the Moon's is the float. */
  readonly gravity: number;
  /**
   * The tier of the planet's radiation zones (`~`, GDD: Radiation). Sol is a tier 1 system, which
   * the Heat Shield covers; its zones are a tier above, so they take Shielding 2.
   */
  readonly radiation: number;
  /** The boss's arena, after the seeded rooms. */
  readonly boss: RoomDef | null;
  /** The suit's word on landing the first time: what is new here. */
  readonly intro: string;
}

export const MOON: Planet = {
  id: 'moon',
  name: 'THE MOON',
  chunks: MOON_CHUNKS,
  rooms: 3,
  gravity: 0.6,
  radiation: 2,
  boss: null,
  intro: 'LOW GRAVITY. JUMPS GO HIGHER AND FALL SLOWER. MIND THE CEILINGS.',
};

/** Mars: dust vents lift the suit for free; the Ranger waits at the end. */
export const MARS: Planet = {
  id: 'mars',
  name: 'MARS',
  chunks: MARS_CHUNKS,
  rooms: 2,
  gravity: 0.85,
  radiation: 2,
  boss: RANGER_ARENA,
  intro: 'DUST VENTS. STAND IN ONE AND IT LIFTS US. NO STARLIGHT NEEDED.',
};

/** The planets of one run through Sol, in the order they are visited. */
export const SOL: readonly Planet[] = [MOON, MARS];

/** Rooms in a visit, counting the boss's arena. */
export function roomsOn(planet: Planet): number {
  return planet.rooms + (planet.boss ? 1 : 0);
}

/** True for the boss's arena (it comes after the seeded rooms). */
export function isBossRoom(planet: Planet, room: number): boolean {
  return planet.boss !== null && room === planet.rooms;
}

/** One room of a planet in a run: seeded from the run, or the boss's arena; in its gravity. */
export function planetRoom(planet: Planet, runSeed: string, room: number): RoomDef {
  const def =
    planet.boss && isBossRoom(planet, room)
      ? planet.boss
      : buildRoom(planet.chunks, roomSeed(runSeed, planet.id, room), `${planet.name} ${room + 1}`);
  return { ...def, gravity: planet.gravity, radiation: planet.radiation };
}
